import React, { ReactElement, useState } from 'react'
import { connect } from 'react-redux'
import { ExtendedOverViewProps } from './OverView.types'
import styles from './OverView.module.scss'
import { faBuilding, faGlobe, faIdBadge, faLock, faShieldAlt, faUsers } from '@fortawesome/pro-light-svg-icons'
import {
    Button,
    ButtonSizeTypes,
    ButtonType,
    Icon,
    ModalTypes,
    SideRail,
    StandardModal,
    Tag,
    TagType
} from '@digicert/dcone-common-ui'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Link, useHistory } from 'react-router-dom'
import { formatDate, getEntityStatus, lastModified, serviceIcon, translate } from '../../../../shared/helpers/utils'
import { Translate } from 'react-localize-redux'
import { RouterConfig } from '../../../../core/utils/constants'
import cn from 'classnames'
import EditOverView from './edit/EditOverView'
import { IAppState } from '../../../../core/store/store'
import { deleteAccount } from '../../Tenants.actions'
import { BasePathConst } from '../../../../configs/Path.configs'
import { IAccessScope } from '../../../../reducers/Reducers.interfaces'
import { ENABLED } from 'core/constants'
import { isAlphabeticWithUnderscoreSpace } from 'core/utils/helpers'
import { getItemFromLS, setItemToLS } from 'shared/helpers/LocalStorage'

const OverView = (
    {
        account,
        localeList,
        canEdit,
        profile,
        updateNav,
        setAccount,
        deleteAccount,
        accessScope
    }: ExtendedOverViewProps):ReactElement => {
    const history = useHistory()
    const [showSideRail, setShowSideRail] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const ssoMethodSaml = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnSaml')
    const standardSignIn = account?.sign_in_methods.find(item => item.signInMethod === 'standard')
    const ssoMethodOidc = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnOidc')
    const [stdSignInEnabled, ] = useState(standardSignIn?.status === ENABLED)
    const [mfaEnabled, ] = useState(standardSignIn?.mfaStatus === ENABLED)
    const [ssoSamlEnabled, ] = useState(ssoMethodSaml?.status === ENABLED)
    const [oidcEnabled, ] = useState(ssoMethodOidc?.status === ENABLED)

    const onDeleteAccount = () => {
        const message = <>
            {translate('notifications.accounts.accountDeleted')} <strong>{account.name}.</strong>
        </>

        deleteAccount(account.id, message).then(() => {
            setShowDeleteModal(false)
        })

        history.push(`${BasePathConst}/accounts/`)
    }

    const updateFilters = (data, newFilters) => {
        return {
          ...data,
          meta: {
            ...data.meta,
            filters: newFilters
          }
        };
      }

    const navigateToUserList = () => {
        const storedTableData = JSON.parse(getItemFromLS('dcone-table/user-table-' + profile?.id) as string)
        if (storedTableData) {
            const defaultFilters = {
                status: [
                  {
                    value: 'LOCKED',
                    func: "eq",
                    label: translate('common.statuses.locked') as string,
                    key: 'common.statuses.locked'
                  },
                  {
                    value: 'PENDING',
                    func: "eq",
                    label: translate('common.statuses.pending') as string,
                    key: 'common.statuses.pending'
                  },
                  {
                    value: 'ACTIVE',
                    func: "eq",
                    label: translate('common.statuses.active') as string,
                    key: 'common.statuses.active'
                  }
                ],
                accountId:  [{value: account.friendly_identifier, label: account.friendly_identifier}]
              }
            setItemToLS('dcone-table/user-table-' + profile?.id, JSON.stringify(updateFilters(storedTableData, defaultFilters)));
        }
        history.push(RouterConfig.administrators.table)
    }

    return (
        <section className={styles.overview}>
            <header className={styles.header}>
                <div className={styles.accountInfo}>
                    <h1>{account.name}</h1>
                    <Tag type={getEntityStatus(account.active) === 'active' ? TagType.ACTIVE : TagType.INACTIVE}>
                        <Translate id={`common.statuses.${getEntityStatus(account.active)}`} />
                    </Tag>
                    <p className={styles.id}><Translate id='accounts.details.accountId' />: {account.id}</p>
                    <p className={styles.details}>
                        <Translate id='common.createdOn'/> {formatDate(account.created_at)} <Translate id='common.at'/> {formatDate(account.created_at, 'h:mm A')} <Translate id='common.by'/> {!isAlphabeticWithUnderscoreSpace(account?.created_by) ? <Link to={RouterConfig.administrators.details(account.created_by)}>{account.created_by_admin}</Link>: account.created_by_admin} &nbsp;|&nbsp; <Translate id='common.lastModified'/> {lastModified(account.updated_at)} <Translate id='common.at'/> {formatDate(account.updated_at, 'h:mm A')} <Translate id='common.by'/> {!isAlphabeticWithUnderscoreSpace(account?.updated_by) ? <Link to={RouterConfig.administrators.details(account.updated_by)}>{account.updated_by_admin}</Link> : account.updated_by_admin}
                    </p>
                </div>
                {accessScope === IAccessScope.system && account.can_delete_account &&(
                    <div className={styles.actions}>
                        <Button buttonType={ButtonType.DESTRUCTIVE} size={ButtonSizeTypes.SMALL}
                                onClick={() => setShowDeleteModal(true)}>
                            <Translate id='common.form.buttons.deleteAccountBtn'/>
                        </Button>
                    </div>
                )}
            </header>

            <div className={styles.overViewOptions}>
                <button className={cn(styles.overViewOption, !canEdit && styles.inActive)} onClick={() => {canEdit ? setShowSideRail(true) : setShowSideRail(false)}}>
                    <p className={styles.label}><Translate id='accounts.details.accountDetails'/></p>
                    <div className={styles.items}>
                        <div className={styles.item}>
                            <Icon icon={faBuilding as IconProp} className={styles.icon}/>
                            <div className={styles.info}>
                                <p className={styles.label}><Translate id='common.accountName'/></p>
                                <p className={styles.name}>{account.name}</p>
                            </div>
                        </div>
                        <div className={styles.item}>
                            <Icon icon={faIdBadge as IconProp} className={styles.icon}/>
                            <div className={styles.info}>
                                <p className={styles.label}><Translate id='common.friendlyId'/></p>
                                <p className={styles.name}>{account.friendly_identifier}</p>
                            </div>
                        </div>
                        <div className={styles.item}>
                            <Icon icon={faGlobe as IconProp} className={styles.icon}/>
                            <div className={styles.info}>
                                <p className={styles.label}><Translate id='profile.language'/></p>
                                <p className={styles.name}>
                                    {account.locale ? translate('profile.languages.' + account.locale) : translate('profile.languages.en_US')}
                                </p>
                            </div>
                        </div>
                    </div>
                </button>

                <button className={styles.servicesViewOption} onClick={() => history.push(RouterConfig.accounts.services(account.id))}>
                    <p className={styles.label}><Translate id='accounts.details.sideNav.services'/></p>
                    <div className={styles.items}>
                        {account.applications.map((item, index) => {
                            return (
                                <div className={styles.item} key={index}>
                                    <div className={styles.icon}>{serviceIcon(item.name)}</div>
                                    <div className={styles.info}>
                                        <p className={styles.name}>{item.name} {item.service_provider_accounts && item.service_provider_accounts.length > 0 ? '('+item.service_provider_accounts.length+')' : null}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </button>

                <button className={styles.signInMethodsOption} onClick={() => history.push(RouterConfig.accounts.signInMethods(account?.id))}>
                    <p className={styles.label}><Translate id='accounts.details.sideNav.sigInMethods'/></p>
                    <div className={styles.items}>
                        {stdSignInEnabled &&
                            <div className={styles.item}>
                                <Icon icon={faLock as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate
                                        id='accounts.details.signInMethods.passwordAuthentication' /></p>
                                </div>
                            </div>
                        }
                        {mfaEnabled &&
                            <div className={styles.item}>
                                <Icon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='accounts.details.signInMethods.2fa' /></p>
                                </div>
                            </div>
                        }
                        {ssoSamlEnabled &&
                            <div className={styles.item}>
                                <Icon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='ssoSaml.singleSignOnWithSaml' /></p>
                                </div>
                            </div>
                        }
                        {oidcEnabled &&
                            <div className={styles.item}>
                                <Icon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='common.form.singleSignOnOidc' /></p>
                                </div>
                            </div>
                        }
                    </div>
                </button>

                <button className={styles.usersOption} onClick={navigateToUserList}>
                    <p className={styles.label}><Translate id='common.users'/></p>
                    <div className={styles.items}>
                        <div className={styles.item}>
                            <Icon icon={faUsers as IconProp} className={styles.icon}/>
                            <div className={styles.info}>
                                <p className={styles.label}><Translate id='common.numberOfUsers'/></p>
                                <p className={styles.name}>{account.admins.length}</p>
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            <SideRail
                className={cn(styles.sideRail, showSideRail && styles.expanded)}
                headline={<h2><Translate id='accounts.details.updateAccountDetails'/></h2>}
                show={showSideRail}
                onDismiss={() => setShowSideRail(!showSideRail)}
                lightbox
            >
                <EditOverView
                    account={account}
                    localeList={localeList}
                    onCancel={() => {setShowSideRail(false)}}
                    accessScope={accessScope}
                    updateNav={updateNav}
                    setAccount={setAccount}
                />
            </SideRail>

            {showDeleteModal &&
                <StandardModal
                    modalType={ModalTypes.DESTRUCTIVE}
                    headline={translate('accounts.delete.title') as string}
                    display={showDeleteModal}
                    buttonLabel={translate('common.form.buttons.deleteAccountBtn') as string}
                    buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
                    onOkCallBack={onDeleteAccount}
                    onCancelCallBack={() => setShowDeleteModal(false)}
                >
                    <p className={styles.label}>
                        <Translate id='accounts.details.deleteAccountDescription'/>
                    </p>
                </StandardModal>
            }
        </section>
    )
}

export default connect(
    (state: IAppState) => ({
        profile: state.profile?.data,
        localeList: state.locales
    }),
    { deleteAccount }
)(OverView)
